import React, { useState, useContext, useEffect } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import Button from '@carvana/showroom-forms/Button/Primary';
import { Select, Option } from '@carvana/showroom-forms/Select';
import TextArea from '@carvana/showroom-forms/Input/TextArea';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { OpenInNewTabWithNativeEvents } from '@components/_common/link/link';
import { Link } from '../../styles';
import { Label, InstructionBox, FormBox, ButtonContainer } from './style';
import { ModalContentWrapper } from '../../../ASRCard.styles';
import { AsrAnalytics } from '../../../../../constants';
import { useRouter, ROUTES, ERRORS } from '../../../providers/router';
import { useCreateAsr } from '../../../providers/create-asr';
import ASRApi from '../../../ASRApi';
import { ErrorBanner } from '../../../components/error-banner';
import { CATEGORIES } from '../../constants';

export const ASRCreateDetails = () => {
  const [asrIssueCategories, setAsrIssueCategories] = useState<any[]>();
  const [asrIssueCategory, setAsrIssueCategory] = useState('');
  const [asrIssueLocation, setAsrIssueLocation] = useState('');
  const [asrIssueType, setAsrIssueType] = useState('');
  const [asrIssueDetails, setAsrIssueDetails] = useState('');
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);

  const { updateAsrIssue } = useCreateAsr();

  const { trackEvent } = useContext(AnalyticsContext);
  const { goToRoute, setError } = useRouter();

  const populateCategories = async () => {
    const res = await ASRApi.getASRIssueCategories();
    if (!res || !res.length) {
      setError(ERRORS.DEFAULT);
    } else {
      setAsrIssueCategories(res);
    }
  };

  useEffect(() => {
    trackEvent(AsrAnalytics.VIEWED_FORM);
    datadogRum.addAction(AsrAnalytics.VIEWED_FORM);
    populateCategories();
  }, []);

  const SELECT_LABEL = 'Select';

  const getSubCategoryOptions = (issueCategoryValue: string) => {
    const issueCategory: any = asrIssueCategories?.find((x: any) => x.name === issueCategoryValue) || {};
    return issueCategory?.locations;
  };

  const getTypeOptions = (subCategoryValue: any) => {
    const foundSubCategory: any = subCategoryOptions.find((y: any) => y.name === subCategoryValue) || {};
    const types = foundSubCategory && foundSubCategory.types;
    return types;
  };

  const submitForm = () => {
    const selectedType = asrIssueType;
    const newASRIssue = {
      category: asrIssueCategory,
      location: asrIssueLocation,
      type: asrIssueType,
      images: [],
      details: asrIssueDetails,
    };

    if (selectedType === CATEGORIES.MECHANICAL) {
      updateAsrIssue(newASRIssue, { shouldSave: true });
    } else {
      updateAsrIssue(newASRIssue);
      goToRoute(ROUTES.COSMETIC_DISCLAIMER);
    }
  };

  const reconditioningStandardsClick = () => {
    trackEvent(AsrAnalytics.CLICKED_RECONDITIONING);
    OpenInNewTabWithNativeEvents({
      url: '/vehicle-reconditioning-standards',
      event: 'link',
    });
  };

  const shouldShowIssueType = asrIssueCategory && asrIssueLocation && typeOptions.length > 1;
  const formComplete = asrIssueCategory && asrIssueLocation && asrIssueType;

  return (
    <ModalContentWrapper>
      <ErrorBanner />
      <InstructionBox>
        Provide details about your issue so we can help. Learn about our{' '}
        <Link rel="noopener noreferrer" onClick={reconditioningStandardsClick}>
          reconditioning standards.
        </Link>
      </InstructionBox>
      <FormBox>
        <Select
          id="asr-category"
          label="Issue Type"
          testId="asr-category-dropdown"
          onOpen={() => {
            trackEvent(AsrAnalytics.EXPANDED_ISSUE_TYPE);
          }}
          value={asrIssueCategory}
          onChange={(e: any) => {
            const category = e?.target?.value;
            setAsrIssueCategory(category);
            setAsrIssueLocation('');
            setAsrIssueType('');
            setTypeOptions([]);
            setSubCategoryOptions(getSubCategoryOptions(category));
            trackEvent(AsrAnalytics.SELECTED_ISSUE_TYPE, { issueType: category });
            datadogRum.addAction(AsrAnalytics.SELECTED_ISSUE_TYPE, { issueType: category });
          }}
        >
          {asrIssueCategories &&
            asrIssueCategories
              .filter((x: any) => x.name !== 'Accessories')
              .map((x: any) => (
                <Option value={x.name} key={x.name}>
                  {x.name}
                </Option>
              ))}
        </Select>
        <Select
          id="asr-location"
          label="Issue Location"
          testId="asr-location-dropdown"
          value={asrIssueLocation}
          onOpen={() => {
            trackEvent(AsrAnalytics.EXPANDED_ISSUE_LOCATION);
          }}
          onChange={(e: any) => {
            const location = e?.target?.value;
            setAsrIssueLocation(location);
            setAsrIssueType('');
            const typeChoice = getTypeOptions(location);
            trackEvent(AsrAnalytics.SELECTED_ISSUE_LOCATION, { issueLocation: location });
            datadogRum.addAction(AsrAnalytics.SELECTED_ISSUE_LOCATION, { issueLocation: location });

            if (typeChoice.length === 1) {
              const typeName = typeChoice[0].name;
              setAsrIssueType(typeName);
              setTypeOptions([]);
              return;
            }

            setTypeOptions(getTypeOptions(location));
          }}
        >
          {subCategoryOptions &&
            subCategoryOptions.map((option: any) => (
              <Option value={option.name} key={option.name}>
                {option.name}
              </Option>
            ))}
        </Select>

        {shouldShowIssueType && (
          <>
            <Label bold>What best describes your concern?</Label>
            <Select
              id="asr-type"
              label={SELECT_LABEL}
              value={asrIssueType}
              testId="asr-type-dropdown"
              onOpen={() => {
                trackEvent(AsrAnalytics.EXPANDED_ISSUE_CATEGORY);
              }}
              onChange={(e: any) => {
                const type = e?.target?.value;
                setAsrIssueType(type);
                trackEvent(AsrAnalytics.SELECTED_ISSUE_CATEGORY, { issueCategory: type });
                datadogRum.addAction(AsrAnalytics.SELECTED_ISSUE_CATEGORY, { issueCategory: type });
              }}
            >
              {typeOptions &&
                typeOptions.map((option: any) => (
                  <Option value={option.name} key={option.name}>
                    {option.name}
                  </Option>
                ))}
            </Select>
          </>
        )}

        <TextArea
          maxLength={200}
          id="details"
          placeholder="Provide more detail here..."
          showCharCount
          testId="asr-details"
          value={asrIssueDetails}
          onFocus={() => {
            trackEvent(AsrAnalytics.DETAIL_FIELD_INTERACTION);
          }}
          onChange={(e: any) => {
            setAsrIssueDetails(e?.target?.value);
          }}
        />

        <ButtonContainer>
          <Button
            onClick={() => {
              submitForm();
              const eventPayload = {
                issueType: asrIssueCategory,
                issueLocation: asrIssueLocation,
                issueCategory: asrIssueType,
                issueDescription: asrIssueDetails,
              };
              trackEvent(AsrAnalytics.CLICKED_SUBMIT, eventPayload);
              datadogRum.addAction(AsrAnalytics.CLICKED_SUBMIT, eventPayload);
            }}
            disabled={!formComplete}
          >
            Submit
          </Button>
        </ButtonContainer>
      </FormBox>
    </ModalContentWrapper>
  );
};

export default ASRCreateDetails;
